import type { Control, FieldValues } from 'react-hook-form';

import type { SDSFlexboxGap } from '@repo/sds/SDSFlexbox';
import { FormField } from '@repo/ui/fixtures/form/components/FormField/FormField';

import { FormGroup } from '../FormGroup/FormGroup';
import type { FormConfigItem } from './Form';

export function getFormConfigItemKey<TFieldValues extends FieldValues>(
  item: FormConfigItem<TFieldValues>,
  fallbackIndex: number,
): string {
  if ('groupTitle' in item) return item.groupTitle;
  if ('componentProps' in item && item.componentProps.name) return item.componentProps.name;
  return `form-config-item-${fallbackIndex}`;
}

interface FormConfigItemRendererProps<
  TFieldValues extends FieldValues,
  TContext,
  TOutputType extends FieldValues = TFieldValues,
> {
  item: FormConfigItem<TFieldValues>;
  control: Control<TFieldValues, TContext, TOutputType>;
  isDisabled: boolean;
  gap: SDSFlexboxGap;
}

export function FormConfigItemRenderer<
  TFieldValues extends FieldValues,
  TContext,
  TOutputType extends FieldValues = TFieldValues,
>({ item, control, isDisabled, gap }: FormConfigItemRendererProps<TFieldValues, TContext, TOutputType>) {
  // Use a type guard ('in' operator) to safely determine if the item is a group config.
  if ('groupTitle' in item) {
    return <FormGroup groupConfig={item} control={control} isDisabled={isDisabled} gap={gap} />;
  }

  // Use a type guard to check if the item is a field config with necessary properties.
  if ('componentProps' in item && item.componentProps.name) {
    return <FormField fieldConfig={item} control={control} isDisabled={isDisabled} gap={gap} />;
  }

  if (process.env.NODE_ENV === 'development') {
    // Unknown item type in formConfig
  }
  // Return null for unknown items to prevent rendering errors.
  return null;
}
